/**
 * ComponentStep - Step 2: Define the first component
 */

import React, { useState } from 'react';
import type { ScratchFormData } from '../ScratchDetailsScreen';

interface ComponentStepProps {
  data: ScratchFormData;
  onChange: (updates: Partial<ScratchFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const COMPONENT_TYPES = [
  { value: 'SYSTEM', label: 'System', description: 'Top-level system' },
  { value: 'SUBSYSTEM', label: 'Subsystem', description: 'Major subsystem' },
  { value: 'COMPONENT', label: 'Component', description: 'Individual component' },
  { value: 'PART', label: 'Part', description: 'Smallest unit' },
] as const;

export function ComponentStep({ data, onChange, onNext, onBack }: ComponentStepProps) {
  const [errors, setErrors] = useState<{ name?: string }>({});

  const handleChange = (field: keyof typeof data.component, value: string) => {
    onChange({
      component: {
        ...data.component,
        [field]: value,
      },
    });

    if (field === 'name' && errors.name) {
      setErrors({});
    }
  };

  const handleNext = () => {
    const newErrors: { name?: string } = {};

    if (!data.component.name || data.component.name.trim().length < 2) {
      newErrors.name = 'Component name must be at least 2 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onNext();
  };

  const isValid = data.component.name.trim().length >= 2;

  return (
    <div className="step-container" data-testid="component-step">
      <div>
        <h2 className="step-title">Add your first component</h2>
        <p className="step-subtitle">
          Start with your top-level system or a key component. You can add more components later in
          the app.
        </p>
      </div>

      <div className="step-form">
        {/* Component Name */}
        <div className="form-field">
          <label htmlFor="component-name">
            Component Name <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            id="component-name"
            type="text"
            value={data.component.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Motor Assembly, Power Supply, Sensor Module"
            data-testid="component-name-input"
          />
          {errors.name && <span className="field-error">{errors.name}</span>}
          <span className="field-hint">Name the component you want to analyze for failures</span>
        </div>

        {/* Component Type */}
        <div className="form-field">
          <label htmlFor="component-type">Component Type</label>
          <select
            id="component-type"
            value={data.component.type}
            onChange={(e) =>
              handleChange('type', e.target.value as ScratchFormData['component']['type'])
            }
            data-testid="component-type-select"
          >
            {COMPONENT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label} - {type.description}
              </option>
            ))}
          </select>
          <span className="field-hint">Select the hierarchy level of this component</span>
        </div>
      </div>

      <div className="step-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onBack}
          data-testid="back-button"
        >
          Back
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNext}
          disabled={!isValid}
          data-testid="next-button"
        >
          Next: Add Failure
        </button>
      </div>
    </div>
  );
}
